import { Skeleton } from 'primereact/skeleton';
import { ResponsiveContainer, PieChart, Pie, Legend } from 'recharts';
import { pCompanyPieChartGraphEntry } from '~/api/def/endpoints/fundAnalyticsEndpoint';
import { formatCurrency } from '../money';
import { useMemo } from 'react';
import { fundAnalyticsDataAtom } from '~/atoms/fundAnalyticsAtom';
import { useAtom } from 'jotai';
import { WidgetActions } from '~/components/ui/common/WidgetActions';

type PCompanyInvestedPieChartProps = {
  widgetId?: string;
  handleRemoveWidget?: (widgetId: string) => void;
};

const renderCurrencyLabel = (data: pCompanyPieChartGraphEntry) => {
  return formatCurrency(data.currency ?? 'EUR', data.totalValuation);
};

const style = {
  top: '50%',
  right: 55,
  transform: 'translate(0, -50%)',
  lineHeight: '24px',
};

export default function PCompanyInvestedPieChart({
  handleRemoveWidget,
  widgetId,
}: PCompanyInvestedPieChartProps) {
  const [fundAnalyticsState] = useAtom(fundAnalyticsDataAtom);

  const noData = useMemo(() => {
    return (
      !fundAnalyticsState.pieChartDate.length ||
      fundAnalyticsState.pieChartDate.every(
        (entry: pCompanyPieChartGraphEntry) => entry.totalValuation === 0,
      )
    );
  }, [fundAnalyticsState.pieChartDate]);

  if (fundAnalyticsState.loading) {
    return (
      <div className="h-full">
        <Skeleton className="h-full"></Skeleton>
      </div>
    );
  }

  const removeWidget = () => {
    widgetId && handleRemoveWidget && handleRemoveWidget(widgetId);
  };

  if (fundAnalyticsState.error) {
    return (
      <div className="hover-container h-full">
        <div className="w-full flex justify-content-between align-items-center">
          <h3 className="mb-0">Invested per Sector (£)</h3>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
          </div>
        </div>
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: 'calc(100% - 3.4rem)' }}
        >
          <div className="p-error">Something went wrong.</div>
        </div>
      </div>
    );
  }

  if (noData) {
    return (
      <div className="hover-container h-full">
        <div className="w-full flex justify-content-between align-items-center">
          <h3 className="mb-0">Invested per Sector (£)</h3>
          <div className="align-items-center flex flex-row align-self-start">
            <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
          </div>
        </div>
        <div
          className="flex justify-content-center align-items-center"
          style={{ height: 'calc(100% - 3.4rem)' }}
        >
          <div>No data found.</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hover-container">
      <div className="w-full flex justify-content-between align-items-center">
        <h3 className="text-lg font-semibold mb-0">Invested per Sector (£)</h3>
        <WidgetActions onRemove={removeWidget} widgetId={widgetId} />
      </div>
      <div className="h-20rem">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart
            width={400}
            height={400}
            className="w-full"
            margin={{ top: 5, right: 250, bottom: 5, left: 5 }}
          >
            <Legend
              payload={fundAnalyticsState.pieChartDate.map((v) => ({
                value: v.sectorName,
                color: v.fill,
              }))}
              iconSize={10}
              layout="vertical"
              verticalAlign="middle"
              wrapperStyle={style}
            />
            <Pie
              data={fundAnalyticsState.pieChartDate}
              dataKey="totalValuation"
              cx="50%"
              cy="50%"
              innerRadius={50}
              fill="#82ca9d"
              label={renderCurrencyLabel}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
